import React, { useState, useEffect } from 'react';
import {
  ShoppingCart, Plus, Edit2, Receipt, Calendar, CheckCircle,
  AlertCircle, Clock, Search, Filter, DollarSign, FileText
} from 'lucide-react';

interface PurchaseOrder {
  id: number;
  order_number: string;
  order_date: string;
  supplier_id: number;
  supplier_name?: string;
  invoice_number?: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  payment_due_date?: string;
  payment_status: 'unpaid' | 'partial' | 'paid';
  payment_date?: string;
  payment_method?: string;
  notes?: string;
  created_at: string;
}

interface Supplier {
  id: number;
  code: string;
  name: string;
  payment_terms?: string;
}

interface InventoryItem {
  id: number;
  item_name: string;
  unit: string;
  cost_per_unit?: number;
}

interface OrderItem {
  inventory_item_id?: number;
  item_name: string;
  quantity: number;
  unit: string;
  unit_price: number;
  amount: number;
}

const PurchaseOrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<PurchaseOrder>>({
    order_date: new Date().toISOString().split('T')[0]
  });
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, suppliersRes, inventoryRes] = await Promise.all([
        fetch('/api/purchase-orders'),
        fetch('/api/suppliers'),
        fetch('/api/inventory-enhanced')
      ]);

      const ordersData = await ordersRes.json();
      const suppliersData = await suppliersRes.json();
      const inventoryData = await inventoryRes.json();

      if (ordersData.success) setOrders(ordersData.data);
      if (suppliersData.success) setSuppliers(suppliersData.data);
      if (inventoryData.success) setInventoryItems(inventoryData.data);
    } catch (err) {
      console.error('データ取得エラー:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      order_date: new Date().toISOString().split('T')[0]
    });
    setOrderItems([{
      item_name: '',
      quantity: 1,
      unit: '',
      unit_price: 0,
      amount: 0
    }]);
  };

  const handleAddItem = () => {
    setOrderItems([...orderItems, {
      item_name: '',
      quantity: 1,
      unit: '',
      unit_price: 0,
      amount: 0
    }]);
  };

  const handleRemoveItem = (index: number) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const newItems = [...orderItems];
    newItems[index] = { ...newItems[index], [field]: value };

    // 金額自動計算
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    }

    setOrderItems(newItems);

    // 合計金額の再計算
    const subtotal = newItems.reduce((sum, item) => sum + item.amount, 0);
    const taxAmount = Math.floor(subtotal * 0.1);
    setFormData({
      ...formData,
      subtotal,
      tax_amount: taxAmount,
      total_amount: subtotal + taxAmount
    });
  };

  const handleSave = async () => {
    if (!formData.order_date || !formData.supplier_id || orderItems.length === 0) {
      alert('必須項目を入力してください');
      return;
    }

    try {
      const response = await fetch('/api/purchase-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          items: orderItems
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('仕入伝票を登録しました');
        setIsCreating(false);
        setFormData({});
        setOrderItems([]);
        fetchData();
      } else {
        throw new Error(data.message);
      }
    } catch (err: any) {
      alert(err.message || '登録に失敗しました');
    }
  };

  const handlePayment = async (orderId: number) => {
    const paymentDate = prompt('支払日を入力してください (YYYY-MM-DD):');
    if (!paymentDate) return;

    const paymentMethod = prompt('支払方法を入力してください (現金/振込/クレジット):');
    if (!paymentMethod) return;

    const paymentAmount = prompt('支払金額を入力してください:');
    if (!paymentAmount) return;

    try {
      const response = await fetch(`/api/purchase-orders/${orderId}/payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_date: paymentDate,
          payment_method: paymentMethod,
          payment_amount: parseFloat(paymentAmount)
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('支払消込を完了しました');
        fetchData();
      } else {
        throw new Error(data.message);
      }
    } catch (err: any) {
      alert(err.message || '支払消込に失敗しました');
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.order_number.includes(searchTerm) ||
      order.supplier_name?.includes(searchTerm) ||
      order.invoice_number?.includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || order.payment_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'partial':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'unpaid':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'paid': return '支払済';
      case 'partial': return '一部支払';
      case 'unpaid': return '未払';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShoppingCart className="w-6 h-6" />
          仕入伝票管理
        </h1>
      </div>

      {!isCreating ? (
        <>
          {/* 検索・フィルターバー */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="伝票番号、仕入先、請求書番号で検索..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="px-4 py-2 border rounded-lg"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">すべて</option>
                <option value="unpaid">未払</option>
                <option value="partial">一部支払</option>
                <option value="paid">支払済</option>
              </select>
              <button
                onClick={handleCreate}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                新規作成
              </button>
            </div>
          </div>

          {/* 買掛金サマリー */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-500 mb-1">未払金額合計</div>
              <div className="text-2xl font-bold text-red-600">
                ¥{orders.filter(o => o.payment_status === 'unpaid')
                  .reduce((sum, o) => sum + o.total_amount, 0).toLocaleString()}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-500 mb-1">今月支払予定</div>
              <div className="text-2xl font-bold text-yellow-600">
                ¥{orders.filter(o => {
                  if (!o.payment_due_date || o.payment_status === 'paid') return false;
                  const due = new Date(o.payment_due_date);
                  const now = new Date();
                  return due.getMonth() === now.getMonth() && due.getFullYear() === now.getFullYear();
                }).reduce((sum, o) => sum + o.total_amount, 0).toLocaleString()}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-500 mb-1">支払済（今月）</div>
              <div className="text-2xl font-bold text-green-600">
                ¥{orders.filter(o => {
                  if (!o.payment_date) return false;
                  const paid = new Date(o.payment_date);
                  const now = new Date();
                  return paid.getMonth() === now.getMonth() && paid.getFullYear() === now.getFullYear();
                }).reduce((sum, o) => sum + o.total_amount, 0).toLocaleString()}
              </div>
            </div>
          </div>

          {/* 伝票リスト */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">伝票番号</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">日付</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">仕入先</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">請求書番号</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">金額</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">支払期日</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">状態</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(order => (
                  <tr key={order.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono">{order.order_number}</td>
                    <td className="px-4 py-3 text-sm">{order.order_date}</td>
                    <td className="px-4 py-3 text-sm">{order.supplier_name}</td>
                    <td className="px-4 py-3 text-sm">{order.invoice_number || '-'}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">
                      ¥{order.total_amount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {order.payment_due_date || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {getStatusIcon(order.payment_status)}
                        <span className="text-sm">{getStatusLabel(order.payment_status)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {order.payment_status !== 'paid' && (
                        <button
                          onClick={() => handlePayment(order.id)}
                          className="text-blue-500 hover:bg-blue-50 px-2 py-1 rounded text-sm"
                        >
                          支払消込
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredOrders.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                該当する仕入伝票がありません
              </div>
            )}
          </div>
        </>
      ) : (
        /* 新規作成フォーム */
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-6">仕入伝票 新規作成</h2>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                仕入日 *
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.order_date}
                onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                仕入先 *
              </label>
              <select
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.supplier_id || ''}
                onChange={(e) => {
                  const supplierId = parseInt(e.target.value);
                  const supplier = suppliers.find(s => s.id === supplierId);
                  if (supplier && supplier.payment_terms) {
                    // 支払期日を自動計算
                    const orderDate = new Date(formData.order_date || new Date());
                    const dueDate = new Date(orderDate);

                    // 簡易的な計算（月末締め翌月末払いの場合）
                    if (supplier.payment_terms.includes('月末締め翌月末')) {
                      dueDate.setMonth(dueDate.getMonth() + 2, 0);
                    } else if (supplier.payment_terms.includes('月末締め翌月15日')) {
                      dueDate.setMonth(dueDate.getMonth() + 1, 15);
                    }

                    setFormData({
                      ...formData,
                      supplier_id: supplierId,
                      payment_due_date: dueDate.toISOString().split('T')[0]
                    });
                  } else {
                    setFormData({ ...formData, supplier_id: supplierId });
                  }
                }}
              >
                <option value="">選択してください</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name} ({supplier.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                請求書番号
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.invoice_number || ''}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                支払期日
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.payment_due_date || ''}
                onChange={(e) => setFormData({ ...formData, payment_due_date: e.target.value })}
              />
            </div>
          </div>

          {/* 明細入力 */}
          <h3 className="text-lg font-semibold mb-4">仕入明細</h3>
          <div className="border rounded-lg p-4 mb-4">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left pb-2">品名</th>
                  <th className="text-left pb-2">数量</th>
                  <th className="text-left pb-2">単位</th>
                  <th className="text-right pb-2">単価</th>
                  <th className="text-right pb-2">金額</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {orderItems.map((item, index) => (
                  <tr key={index} className="border-b">
                    <td className="py-2">
                      <input
                        type="text"
                        className="w-full px-2 py-1 border rounded"
                        value={item.item_name}
                        onChange={(e) => handleItemChange(index, 'item_name', e.target.value)}
                        placeholder="品名を入力"
                      />
                    </td>
                    <td className="py-2">
                      <input
                        type="number"
                        className="w-20 px-2 py-1 border rounded"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                        min="0"
                        step="0.1"
                      />
                    </td>
                    <td className="py-2">
                      <input
                        type="text"
                        className="w-20 px-2 py-1 border rounded"
                        value={item.unit}
                        onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                        placeholder="個/kg"
                      />
                    </td>
                    <td className="py-2 text-right">
                      <input
                        type="number"
                        className="w-24 px-2 py-1 border rounded text-right"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value))}
                        min="0"
                      />
                    </td>
                    <td className="py-2 text-right font-semibold">
                      ¥{item.amount.toLocaleString()}
                    </td>
                    <td className="py-2 pl-2">
                      <button
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-500 hover:bg-red-50 p-1 rounded"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <button
              onClick={handleAddItem}
              className="mt-2 text-blue-500 hover:bg-blue-50 px-3 py-1 rounded text-sm"
            >
              + 明細追加
            </button>
          </div>

          {/* 合計 */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between">
              <span>小計</span>
              <span className="font-semibold">¥{(formData.subtotal || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span>消費税(10%)</span>
              <span className="font-semibold">¥{(formData.tax_amount || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-lg font-bold">
              <span>合計</span>
              <span>¥{(formData.total_amount || 0).toLocaleString()}</span>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              備考
            </label>
            <textarea
              className="w-full px-3 py-2 border rounded-lg"
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={() => {
                setIsCreating(false);
                setFormData({});
                setOrderItems([]);
              }}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              キャンセル
            </button>
            <button
              onClick={handleSave}
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
              disabled={!formData.order_date || !formData.supplier_id || orderItems.length === 0}
            >
              登録
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderManagement;